import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  AutocompleteOption,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  FormControl,
  FormLabel,
  Input,
  LinearProgress,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
} from '@mui/joy';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import { useTheme } from '@mui/joy/styles';

import {
  fetchAdminUsers,
  updateAdminUserEmail,
  updateAdminUserLegalName,
  updateAdminUserPhone,
  type AdminUser,
} from '../api';
import { useSnackbarStore } from '../store/useSnackbarStore';

export default function AdminUserBulkEditPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { showSnackbar } = useSnackbarStore();

  // State for search and selection
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<AdminUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);

  // State for editing
  const [formData, setFormData] = useState({
    legalName: '',
    email: '',
    phone: '',
  });
  const [selectedField, setSelectedField] = useState<'legalName' | 'email' | 'phone' | ''>('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Handle search
  const handleSearch = React.useCallback(async (value: string) => {
    setSearchQuery(value);
    if (!value.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const results = await fetchAdminUsers(value.trim(), 20);
      setSearchResults(results || []);
    } catch (error) {
      console.error('Failed to search users', error);
      showSnackbar('Failed to search users', 'danger');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [showSnackbar]);

  // Handle user selection
  const handleUserSelect = (user: AdminUser | null) => {
    setSelectedUser(user);
    if (user) {
      setFormData({
        legalName: user.legalName || '',
        email: user.email || '',
        phone: user.phone || '',
      });
      setIsEditing(true);
    } else {
      setFormData({ legalName: '', email: '', phone: '' });
      setIsEditing(false);
    }
    setSelectedField('');
    setSearchQuery('');
    setSearchResults([]);
  };

  // Handle form change
  const handleFormChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Check for changes
  const hasChanges = selectedUser && selectedField && (
    (selectedField === 'legalName' && formData.legalName !== (selectedUser.legalName || '')) ||
    (selectedField === 'email' && formData.email !== (selectedUser.email || '')) ||
    (selectedField === 'phone' && formData.phone !== (selectedUser.phone || ''))
  );

  // Handle save
  const handleSave = async () => {
    if (!selectedUser) return;
    if (!selectedField) {
      showSnackbar('Select a field to update first.', 'warning');
      return;
    }

    // Validate email format
    if (selectedField === 'email' && formData.email && !formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      showSnackbar('Invalid email format', 'warning');
      return;
    }

    // Validate phone format (E.164)
    if (selectedField === 'phone' && formData.phone && !formData.phone.match(/^\+?[1-9]\d{1,14}$/)) {
      showSnackbar('Phone must be in E.164 format (e.g., +91-XXXXXXXXXX)', 'warning');
      return;
    }

    setShowConfirm(true);
  };

  // Handle confirm save
  const handleConfirmSave = async () => {
    if (!selectedUser) return;

    setIsSaving(true);
    try {
      if (selectedField === 'legalName' && formData.legalName !== (selectedUser.legalName || '')) {
        await updateAdminUserLegalName(selectedUser.id, formData.legalName);
      }
      if (selectedField === 'email' && formData.email !== (selectedUser.email || '')) {
        await updateAdminUserEmail(selectedUser.id, formData.email);
      }
      if (selectedField === 'phone' && formData.phone !== (selectedUser.phone || '')) {
        await updateAdminUserPhone(selectedUser.id, formData.phone);
      }
      showSnackbar('User updated successfully', 'success');
      setShowConfirm(false);
      setSelectedUser(null);
      setFormData({ legalName: '', email: '', phone: '' });
      setIsEditing(false);
      setSelectedField('');
    } catch (error) {
      console.error('Failed to update user', error);
      showSnackbar('Failed to update user', 'danger');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setSelectedUser(null);
    setFormData({ legalName: '', email: '', phone: '' });
    setIsEditing(false);
    setSelectedField('');
  };

  const getUserLabel = React.useCallback((user: AdminUser) => {
    const name = user.legalName || user.email || 'Unknown user';
    const email = user.email || 'no-email';
    return `${name} (${email})`;
  }, []);

  const heroBackground = isDark
    ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(10, 15, 26, 0.98) 100%)'
    : 'radial-gradient(120% 140% at 8% 0%, rgba(255, 218, 170, 0.2) 0%, rgba(255, 218, 170, 0) 55%), radial-gradient(120% 140% at 95% 5%, rgba(165, 197, 255, 0.2) 0%, rgba(165, 197, 255, 0) 60%), linear-gradient(180deg, rgba(255, 252, 245, 1) 0%, rgba(248, 250, 255, 1) 45%, rgba(244, 246, 252, 1) 100%)';
  const panelCool = isDark
    ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.95))'
    : 'linear-gradient(135deg, rgba(224, 243, 255, 0.8), rgba(241, 249, 255, 0.95))';

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          background: heroBackground,
          pt: 6,
          pb: 6,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: isDark
              ? 'radial-gradient(120% 120% at 15% -10%, rgba(56, 189, 248, 0.14), transparent 60%), radial-gradient(120% 120% at 100% 0%, rgba(251, 191, 36, 0.12), transparent 55%)'
              : 'radial-gradient(120% 120% at 15% -10%, rgba(255, 220, 160, 0.45), transparent 60%), radial-gradient(120% 120% at 100% 0%, rgba(129, 158, 255, 0.28), transparent 55%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container
          maxWidth="md"
          sx={{
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Stack spacing={2}>
            <Typography
              level="h1"
              sx={{
                fontSize: 'clamp(2rem, 10vw, 3rem)',
                fontWeight: 'bold',
                color: isDark ? 'rgba(248,250,252,0.94)' : 'text.primary',
              }}
            >
              Bulk User Updates
            </Typography>
            <Typography
              level="body-lg"
              sx={{
                color: isDark ? 'rgba(226,232,240,0.8)' : 'text.secondary',
              }}
            >
              Search for users and update their legal name, email, and phone number across all systems including Firebase.
            </Typography>
          </Stack>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Stack spacing={3}>
          {/* Search Card */}
          <Card
            variant="outlined"
            sx={{
              background: panelCool,
              backdropFilter: 'blur(8px)',
            }}
          >
            <CardContent>
              <Typography component="h2" level="h4" sx={{ mb: 2 }}>
                Step 1: Find User
              </Typography>

              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Search by name, email, or phone</FormLabel>
                <Autocomplete
                  placeholder="Start typing to search..."
                  value={selectedUser}
                  onChange={(_, newValue) => handleUserSelect(newValue)}
                  inputValue={searchQuery}
                  onInputChange={(_, newValue) => handleSearch(newValue)}
                  options={searchResults}
                  getOptionLabel={getUserLabel}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  renderOption={(props, option) => (
                    <AutocompleteOption {...props}>
                      <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                        <Avatar
                          src={option.photoUrl}
                          sx={{ width: 32, height: 32 }}
                        />
                        <Stack sx={{ flex: 1 }}>
                          <Typography level="body-sm" fontWeight="500">
                            {option.legalName || option.email || 'Unknown user'}
                          </Typography>
                          <Typography level="body-xs" color="neutral">
                            {option.email || 'no-email'}
                          </Typography>
                        </Stack>
                      </Stack>
                    </AutocompleteOption>
                  )}
                  loading={isSearching}
                  slotProps={{
                    listbox: { sx: { maxHeight: '300px' } },
                  }}
                />
              </FormControl>

              {selectedUser && (
                <Card variant="soft" color="success">
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar src={selectedUser.photoUrl} size="lg" />
                    <Stack sx={{ flex: 1 }}>
                      <Typography fontWeight="500">
                        {selectedUser.legalName || selectedUser.email || 'Unknown user'}
                      </Typography>
                      <Typography level="body-sm" color="neutral">
                        {selectedUser.email}
                      </Typography>
                    </Stack>
                    <CheckCircleOutlinedIcon sx={{ color: 'success.main' }} />
                  </Stack>
                </Card>
              )}
            </CardContent>
          </Card>

          {/* Edit Card */}
          {selectedUser && isEditing && (
            <Card
              variant="outlined"
              sx={{
                background: panelCool,
                backdropFilter: 'blur(8px)',
              }}
            >
              <CardContent>
                <Typography component="h2" level="h4" sx={{ mb: 2 }}>
                  Step 2: Update Information
                </Typography>

                <Stack spacing={2}>
                  <FormControl>
                    <FormLabel>Field to update</FormLabel>
                    <Select
                      placeholder="Select a field"
                      value={selectedField}
                      onChange={(_, value) => setSelectedField((value ?? '') as typeof selectedField)}
                    >
                      <Option value="legalName">Legal name</Option>
                      <Option value="email">Email</Option>
                      <Option value="phone">Phone</Option>
                    </Select>
                  </FormControl>

                  {selectedField === 'legalName' && (
                    <FormControl>
                      <FormLabel>Legal Name</FormLabel>
                      <Input
                        value={formData.legalName}
                        onChange={(e) =>
                          handleFormChange('legalName', e.target.value)
                        }
                        placeholder="Legal name"
                      />
                    </FormControl>
                  )}

                  {selectedField === 'email' && (
                    <FormControl>
                      <FormLabel>Email</FormLabel>
                      <Input
                        value={formData.email}
                        onChange={(e) =>
                          handleFormChange('email', e.target.value)
                        }
                        placeholder="Email address"
                        type="email"
                      />
                    </FormControl>
                  )}

                  {selectedField === 'phone' && (
                    <FormControl>
                      <FormLabel>Phone (E.164 format)</FormLabel>
                      <Input
                        value={formData.phone}
                        onChange={(e) =>
                          handleFormChange('phone', e.target.value)
                        }
                        placeholder="+91 XXXXXXXXXX"
                        slotProps={{
                          input: {
                            type: 'tel',
                          },
                        }}
                      />
                    </FormControl>
                  )}

                  <Divider />

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="solid"
                      startDecorator={<SaveRoundedIcon />}
                      onClick={handleSave}
                      disabled={!hasChanges || isSaving || !selectedField}
                    >
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button
                      variant="outlined"
                      color="neutral"
                      startDecorator={<CancelRoundedIcon />}
                      onClick={handleCancel}
                      disabled={isSaving}
                    >
                      Cancel
                    </Button>
                  </Box>

                  {!hasChanges && selectedUser && (
                    <Typography level="body-sm" color="neutral">
                      {selectedField ? 'No changes detected' : 'Select a field to update'}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}
        </Stack>
      </Container>

      {/* Confirmation Modal */}
      <Modal open={showConfirm} onClose={() => setShowConfirm(false)}>
        <ModalDialog variant="outlined" role="alertdialog">
          <Typography level="h4" component="h2" sx={{ mb: 2 }}>
            Confirm Update
          </Typography>

          {selectedUser && (
            <Stack spacing={2} sx={{ mb: 3 }}>
              <Typography level="body-sm">
                You are about to update the following for <strong>{selectedUser.legalName || selectedUser.email || 'this user'}</strong>:
              </Typography>
              <Stack spacing={1} sx={{ ml: 2 }}>
                {selectedField === 'legalName' && formData.legalName !== (selectedUser.legalName || '') && (
                  <Typography level="body-sm">
                    <strong>Legal Name:</strong> {selectedUser.legalName || '(none)'} →{' '}
                    {formData.legalName || '(none)'}
                  </Typography>
                )}
                {selectedField === 'email' && formData.email !== (selectedUser.email || '') && (
                  <Typography level="body-sm">
                    <strong>Email:</strong> {selectedUser.email || '(none)'} →{' '}
                    {formData.email || '(none)'}
                  </Typography>
                )}
                {selectedField === 'phone' && formData.phone !== (selectedUser.phone || '') && (
                  <Typography level="body-sm">
                    <strong>Phone:</strong> {selectedUser.phone || '(none)'} →{' '}
                    {formData.phone || '(none)'}
                  </Typography>
                )}
              </Stack>
              <Typography level="body-sm" color="warning">
                This will update the user record in the database and synchronize email and phone with Firebase Auth.
              </Typography>
            </Stack>
          )}

          <Stack direction="row" spacing={1}>
            <Button
              variant="solid"
              color="primary"
              onClick={handleConfirmSave}
              loading={isSaving}
            >
              Confirm Update
            </Button>
            <Button
              variant="outlined"
              color="neutral"
              onClick={() => setShowConfirm(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
}
